import type { CondicaoPagamentoResponse, OrcamentoReformaExibicao, TabelaPrecoResponse } from '@triade/contracts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { REGIME_ROTULO } from '@/components/reforma/reforma-ui';
import { UFS_BR } from '@/lib/ufs';
import { ClienteCombobox } from './cliente-combobox';
import { ufAoEscolherCliente, type FormOrcamento } from './tipos';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRIGGER = 'h-10 w-full rounded-[10px] bg-card';
/** Valor interno do item "Sem UF" (o Radix Select nao aceita valor vazio). */
const SEM_UF = '__sem_uf';

export interface DadosGeraisProps {
  form: FormOrcamento;
  set: <K extends keyof FormOrcamento>(campo: K, valor: FormOrcamento[K]) => void;
  editavel: boolean;
  tabelas: { data: TabelaPrecoResponse[] | undefined; isPending: boolean };
  condicoes: { data: CondicaoPagamentoResponse[] | undefined; isPending: boolean };
  /** Reforma da exibicao atual: regime que a data de faturamento decide. */
  reforma?: OrcamentoReformaExibicao;
}

/** Cabecalho do orcamento: cliente, data, tabela, condicao e descricao. So entrada de dados; nada e calculado aqui. */
export function DadosGerais({ form, set, editavel, tabelas, condicoes, reforma }: DadosGeraisProps) {
  const carregando = tabelas.isPending || condicoes.isPending;
  return (
    <section aria-label="Dados gerais" className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5 rounded-2xl border border-border bg-card p-4">
      <div className="min-w-0 sm:col-span-2">
        <Label htmlFor="o-cliente" className={ROTULO}>
          Cliente
        </Label>
        <ClienteCombobox
          id="o-cliente"
          valor={form.cliente}
          onSelecionar={(c) => {
            set('cliente', c);
            // Sem escolha manual da UF, ela acompanha o endereco principal do cliente escolhido.
            set('ufDestino', ufAoEscolherCliente(form, c));
          }}
          disabled={!editavel}
        />
      </div>
      <div>
        <Label htmlFor="o-uf" className={ROTULO}>
          UF de destino
        </Label>
        <Select
          value={form.ufDestino || SEM_UF}
          onValueChange={(v) => {
            set('ufDestino', v === SEM_UF ? '' : v);
            set('ufDestinoManual', true);
          }}
          disabled={!editavel}
        >
          <SelectTrigger id="o-uf" aria-label="UF de destino" aria-describedby="o-uf-ajuda" className={TRIGGER}>
            <SelectValue placeholder="Sem UF" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SEM_UF}>Sem UF</SelectItem>
            {UFS_BR.map((uf) => (
              <SelectItem key={uf} value={uf}>
                {uf}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p id="o-uf-ajuda" className="mt-1 text-caption text-ink-muted">
          Vem do endereço principal do cliente; troque se a operação for para outra UF.
        </p>
      </div>
      <div>
        <Label htmlFor="o-data" className={ROTULO}>
          Data
        </Label>
        <Input id="o-data" type="date" value={form.data} onChange={(e) => set('data', e.target.value)} disabled={!editavel} />
      </div>
      <div>
        <Label htmlFor="o-faturamento" className={ROTULO}>
          Data prevista de faturamento
        </Label>
        <Input
          id="o-faturamento"
          type="date"
          value={form.dataFaturamento || form.data}
          onChange={(e) => set('dataFaturamento', e.target.value)}
          disabled={!editavel}
        />
        {reforma && (
          <p data-testid="regime-faturamento" className="mt-1 text-caption text-ink-muted">
            Regime: {reforma.etapa} · {REGIME_ROTULO[reforma.regime]}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="o-tabela" className={ROTULO}>
          Tabela de preço
        </Label>
        <Select value={form.tabelaId} onValueChange={(v) => set('tabelaId', v)} disabled={!editavel || carregando}>
          <SelectTrigger id="o-tabela" className={TRIGGER}>
            <SelectValue placeholder={tabelas.isPending ? 'Carregando…' : 'Escolha a tabela'} />
          </SelectTrigger>
          <SelectContent>
            {(tabelas.data ?? [])
              .filter((t) => t.ativa || t.id === form.tabelaId)
              .map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.nome}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {tabelas.data?.length === 0 && <p className="mt-1 text-caption text-ink-muted">Nenhuma tabela de preço cadastrada.</p>}
      </div>
      <div>
        <Label htmlFor="o-cond" className={ROTULO}>
          Condição de pagamento
        </Label>
        <Select value={form.condicaoId} onValueChange={(v) => set('condicaoId', v)} disabled={!editavel || carregando}>
          <SelectTrigger id="o-cond" className={TRIGGER}>
            <SelectValue placeholder={condicoes.isPending ? 'Carregando…' : 'Escolha a condição'} />
          </SelectTrigger>
          <SelectContent>
            {(condicoes.data ?? [])
              .filter((c) => c.ativa || c.id === form.condicaoId)
              .map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.nome}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {condicoes.data?.length === 0 && <p className="mt-1 text-caption text-ink-muted">Nenhuma condição de pagamento cadastrada.</p>}
      </div>
      <div className="col-span-full">
        <Label htmlFor="o-desc" className={ROTULO}>
          Descrição
        </Label>
        <Input
          id="o-desc"
          value={form.descricao}
          maxLength={300}
          onChange={(e) => set('descricao', e.target.value)}
          disabled={!editavel}
          placeholder="Ex.: reposição de tijolos refratários, linha 2"
        />
      </div>
    </section>
  );
}
